/**
 * Un `<input type="datetime-local">` lu et écrit dans le fuseau de l'ÉDITION.
 *
 * Le champ HTML n'a pas de fuseau : il montre l'heure « murale » de
 * l'appareil. Un organisateur qui prépare depuis Douala une soirée à Berlin
 * saisissait 18:00 et enregistrait 18:00 heure de Douala — une heure d'écart
 * sur le compte à rebours, sans que rien ne le signale. Ces deux fonctions
 * font l'aller-retour dans le fuseau donné (`events.timezone`).
 */

function parts(date: Date, timeZone: string): Record<string, number> {
  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const out: Record<string, number> = {}
  for (const part of formatted) {
    if (part.type !== 'literal') out[part.type] = Number(part.value)
  }
  return out
}

/** Décalage (ms) du fuseau par rapport à UTC, à cet instant. */
function offsetAt(date: Date, timeZone: string): number {
  const p = parts(date, timeZone)
  const asUtc = Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!, p.second!)
  return asUtc - Math.floor(date.getTime() / 1000) * 1000
}

/** ISO → « 2026-11-14T18:00 » tel qu'on le lit à l'heure de l'édition. */
export function toZonedInput(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const p = parts(date, timeZone)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${p.year}-${pad(p.month!)}-${pad(p.day!)}T${pad(p.hour!)}:${pad(p.minute!)}`
}

/**
 * « 2026-11-14T18:00 » saisi à l'heure de l'édition → instant ISO (UTC).
 *
 * Deux passes : le décalage dépend de l'instant (heure d'été), et l'instant
 * dépend du décalage. La seconde passe corrige le cas d'une saisie qui tombe
 * de l'autre côté d'un changement d'heure.
 */
export function fromZonedInput(value: string, timeZone: string): string | null {
  if (!value) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value)
  if (!match) return null
  const [, y, mo, d, h, mi] = match.map(Number) as [number, number, number, number, number, number]
  const wall = Date.UTC(y, mo - 1, d, h, mi)
  let instant = wall - offsetAt(new Date(wall), timeZone)
  instant = wall - offsetAt(new Date(instant), timeZone)
  const date = new Date(instant)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}
